import { CustomError, FilteredCreditApplication } from '@applyonline/shared'
import { replaceLastSpaceWithNbsp } from '@applyonline/shared/utils'
import { EApplicantType, EDocumentStatus, EDocumentType, ERequiredFrom } from '@if/core/types'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { useRequiredDocumentsWithStatus } from '../data/credit-application-hooks'
import { hasApplicantCompletedFlinks, hasApplicantStartedFlinksAnalysis } from '../data/credit-application-selectors'
import UploadFileButton from './UploadFileButton'
interface Props {
  creditApp: FilteredCreditApplication
  applicantType: EApplicantType
}

const RequiredDocumentTable = ({ creditApp, applicantType }: Props) => {
  const { t, i18n } = useTranslation()
  const [error, setError] = useState<CustomError>()
  const navigate = useNavigate()

  const getStatusClass = (status: EDocumentStatus) => {
    switch (status) {
      case EDocumentStatus.AwaitingDocument:
        return 'text-statusBlue'
      case EDocumentStatus.AwaitingApproval:
        return 'text-statusYellow'
      case EDocumentStatus.Incomplete:
        return 'text-statusYellow'
      case EDocumentStatus.Refused:
        return 'text-statusRed'
      case EDocumentStatus.Approved:
        return 'text-statusGreen'
      default:
        return ''
    }
  }

  const applicant =
    applicantType === EApplicantType.Applicant
      ? `${creditApp.applicant.firstName} ${creditApp.applicant.lastName}`
      : creditApp.coapplicant
        ? `${creditApp.coapplicant.firstName} ${creditApp.coapplicant.lastName}`
        : ''

  const reqDocList = useRequiredDocumentsWithStatus(creditApp.requiredDocuments, creditApp.documents)
  const requiredDocuments = reqDocList.filter(
    (d) =>
      d.applicantType == applicantType &&
      d.typeId !== EDocumentType.SignedCVT &&
      d.status !== EDocumentStatus.Approved &&
      d.documentRequiredFrom == ERequiredFrom.Customer,
  )

  const hasRefusedDocument = requiredDocuments.some((doc) => doc.status === EDocumentStatus.Refused)

  const hasIncompletedDocument = requiredDocuments.some((doc) => doc.status === EDocumentStatus.Incomplete)

  const hasApplicantFinishedFlinks = hasApplicantCompletedFlinks(creditApp, applicantType)

  const hasApplicantStartedFlinks = hasApplicantStartedFlinksAnalysis(creditApp, applicantType)

  const hasSubmittedVoidCheque = requiredDocuments.some(
    (doc) =>
      doc.typeId === EDocumentType.VoidCheque &&
      [EDocumentStatus.Approved, EDocumentStatus.AwaitingApproval, EDocumentStatus.AwaitingScan].includes(doc.status),
  )

  return (
    <div className="scroll-container">
      <h3> {applicant && `${applicant.charAt(0).toUpperCase()}${applicant.slice(1)}`}</h3>
      <table className="styled-table">
        <thead>
          <tr>
            <th>{t('uploadRequiredFile.documentName')}</th>
            <th>{t('uploadRequiredFile.status')}</th>
            <th>{t('uploadRequiredFile.upload')}</th>
          </tr>
        </thead>
        <tbody>
          {requiredDocuments.map((doc) => (
            <tr key={doc.id}>
              <td data-label={t('common.document')}>
                {i18n.language === 'fr' ? replaceLastSpaceWithNbsp(doc.nameFr) : replaceLastSpaceWithNbsp(doc.nameEn)}

                <br />
                <span className="table-note">{doc.note ?? ''}</span>
              </td>
              <td className="fixed-width-250" data-label={t('uploadRequiredFile.status')}>
                {error && error.id === doc.id ? (
                  <span className={getStatusClass(EDocumentStatus.Refused)}>
                    {t(`EDocumentStatus.${EDocumentStatus.Refused}`, { ns: 'enum' })}: {error.message}
                  </span>
                ) : hasApplicantStartedFlinks && doc.typeId === EDocumentType.VoidCheque ? (
                  <span className={getStatusClass(EDocumentStatus.AwaitingApproval)}>
                    {' '}
                    {t(`EDocumentStatus.${EDocumentStatus.AwaitingApproval}`, { ns: 'enum' })}
                  </span>
                ) : (
                  <span className={getStatusClass(doc.status)}>
                    {t(`EDocumentStatus.${doc.status}`, { ns: 'enum' })}
                  </span>
                )}
                {(hasRefusedDocument || hasIncompletedDocument) &&
                  (window.innerWidth > 600 ? (
                    <>
                      <br />
                      <span
                        className="table-note"
                        style={{ color: '#E97472', textAlign: 'justify', paddingRight: '10px' }}
                      >
                        {(doc.status === EDocumentStatus.Refused || doc.status === EDocumentStatus.Incomplete) &&
                          doc.refusalReason}
                      </span>
                    </>
                  ) : (
                    doc.refusalReason !== null && (
                      <>
                        <br />
                        <span
                          className="table-note"
                          style={{
                            color: '#E97472',
                            textAlign: 'justify',
                          }}
                        >
                          {(doc.status === EDocumentStatus.Refused || doc.status === EDocumentStatus.Incomplete) &&
                            doc.refusalReason}
                        </span>
                      </>
                    )
                  ))}
              </td>
              <td data-label={t('uploadRequiredFile.upload')}>
                <div
                  onClick={() => {
                    if (
                      doc.typeId === EDocumentType.VoidCheque &&
                      !hasSubmittedVoidCheque &&
                      !hasApplicantFinishedFlinks &&
                      !hasApplicantStartedFlinks
                    ) {
                      navigate(`/creditApplication/${creditApp.id}/${applicantType.toLowerCase()}-incomes`)
                    }
                  }}
                >
                  <UploadFileButton
                    id={doc.id}
                    creditApplicationId={creditApp.id}
                    typeId={doc.typeId}
                    subKey={doc.subKey}
                    applicantType={doc.applicantType}
                    disabled={
                      doc.status === EDocumentStatus.Approved ||
                      (!hasApplicantFinishedFlinks &&
                        !hasSubmittedVoidCheque &&
                        doc.typeId === EDocumentType.VoidCheque)
                    }
                    setError={setError}
                  />
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
export default RequiredDocumentTable
